import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../api';
import { ABG } from './abg';  
import API_BASE_URL from '../apiConfig';
import './Profile.css';
import { commonIcons, getFlag } from './assetImports';
import { useTranslation } from 'react-i18next';

const Profile = ({ wallets, setWallets, isTelegramMiniApp }) => {
    const navigate = useNavigate();
    const { user, updateUserDetails } = useContext(AuthContext);
    const { t, ready } = useTranslation('profile');
    const [nickname, setNickname] = useState(user ? user.nickname || user.username : '');
    const [country, setCountry] = useState(user ? user.country || '' : '');
    const [showCountryPopup, setShowCountryPopup] = useState(false);
    const nicknameTimerRef = useRef(null);
    const [recentGames, setRecentGames] = useState([]);
    const [loadingGames, setLoadingGames] = useState(false);
    const [showAvatarPicker, setShowAvatarPicker] = useState(false);


    const renderAvatar = () => {
        if (user?.customAvatar) {
        return (
            <img
                src={user.customAvatar}
                alt={`${user.nickname}'s Custom Avatar`}
                className="profilePage-avatar-image"
            />
        );
    }
        if (user?.photoUrl) {
            return <img src={user.photoUrl} alt={`${user.nickname}'s Avatar`} className="profilePage-avatar-image" />;
        }
        const initial = user?.nickname?.charAt(0).toUpperCase() || 'P';
        const avatarStyle = ABG(initial);
        return <div className="profilePage-avatar-placeholder" style={avatarStyle}>{initial}</div>;
    };

    const escapeHTML = (input) => {
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
        };
        return input.replace(/[&<>"']/g, (char) => map[char]);
    };

    const handleCountrySelect = async (selectedCountryCode) => {
        setCountry(selectedCountryCode);
        setShowCountryPopup(false);
        
        try {
            const payload = {
                country: selectedCountryCode === '' ? null : selectedCountryCode
            };
            const response = await apiRequest(`${API_BASE_URL}/api/update-profile`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                updateUserDetails();
            }
        } catch (err) {
            console.error('Error updating country:', err);
        }
    };

    const handleDepositClick = () => {
        navigate('/deposit-withdraw-All');
    };

      const handleTokenClick = () => {
        navigate('/Token-To-Usdt');
    };

    useEffect(() => {
        const handleTouchMove = (e) => {
            if (showCountryPopup && !e.target.closest('.profilePage-popup-scroll-container')) {
              e.preventDefault();
            }
          };
          document.addEventListener('touchmove', handleTouchMove, { passive: false });

        if (showCountryPopup) {
          const scrollY = window.scrollY || document.documentElement.scrollTop;
          document.body.style.position = 'fixed';
          document.body.style.top = `-${scrollY}px`;
          document.body.style.width = '100%';
          document.body.style.overflowY = 'hidden';
          
          return () => {
              document.removeEventListener('touchmove', handleTouchMove);
              document.body.style.position = '';
              document.body.style.top = '';
              document.body.style.width = '';
              document.body.style.overflowY = '';
              window.scrollTo(0, scrollY);
          };
        }
      }, [showCountryPopup]);

    const handleNicknameChange = (e) => {
        const newNickname = e.target.value;
        setNickname(newNickname);

        if (nicknameTimerRef.current) {
            clearTimeout(nicknameTimerRef.current);
        }

        nicknameTimerRef.current = setTimeout(async () => {
            try {
                const sanitizedNickname = escapeHTML(newNickname);
                const payload = {
                    nickname: sanitizedNickname
                };
                const response = await apiRequest(`${API_BASE_URL}/api/update-profile`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });

                if (response.ok) {
                    updateUserDetails();
                }
            } catch (err) {
                console.error('Error updating nickname:', err);
            }
        }, 1000);
    };

    useEffect(() => {
        const fetchRecentGames = async () => {
            try {
                setLoadingGames(true);
                const response = await apiRequest(
                    `${API_BASE_URL}/api/recent-games/${user._id}?limit=5`, 
                    {
                        method: 'GET',
                        credentials: 'include',
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    const filteredGames = data.games.filter(game => {
                        return isTelegramMiniApp 
                            ? ['stars', 'token'].includes(game.currency)
                            : ['usdt', 'token'].includes(game.currency);
                    });
                    setRecentGames(filteredGames);
                }
            } catch (err) {
                console.error('Error fetching recent games:', err);
            } finally {
                setLoadingGames(false);
            }
        };

        if (user && user._id) {
            fetchRecentGames();
        }

        return () => {
            if (nicknameTimerRef.current) {
                clearTimeout(nicknameTimerRef.current);
            }
        };
    }, [user._id]);

    const formatBalance = (balance, isUsdt = false) => {
        if (isUsdt) {
            return parseFloat(balance).toFixed(2);
        }
        return parseInt(balance).toString();
    };

    const groupGamesByDate = (games) => {
    if (!ready) return {};
    
    const months = t('months', { returnObjects: true });

     return games.reduce((acc, game) => {
        const dateObj = new Date(game.date);
        const monthIndex = dateObj.getMonth();
        const monthName = months[monthIndex] || '';
        const dateKey = `${dateObj.getDate()} ${monthName}`;

        const formattedTime = `${String(dateObj.getDate()).padStart(2, '0')}.${String(
            dateObj.getMonth() + 1
        ).padStart(2, '0')}.${dateObj.getFullYear()}, ${String(dateObj.getHours()).padStart(
            2,
            '0'
        )}:${String(dateObj.getMinutes()).padStart(2, '0')}:${String(
            dateObj.getSeconds()
        ).padStart(2, '0')}`;

        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push({
            ...game,
            formattedTime  
        });
        return acc;
    }, {});
};

const handleAvatarSelect = async (avatarUrl) => {
    try {
        const payload = { customAvatar: avatarUrl }; // null if selecting default
        const response = await apiRequest(`${API_BASE_URL}/api/update-profile`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        if (response.ok) {
            setShowAvatarPicker(false); 
            updateUserDetails();
        }
    } catch (err) {
        console.error('Failed to update avatar', err);
    }
};


    if (!ready) {
        return (
            <div className="profilePage-container">
                <div className="profilePage-loading-message">{t('loading')}</div>
            </div>
        );
    }

    return (
        <div className="profilePage-container">
            {/* Avatar Section */}
            <div className="profilePage-avatar-container" onClick={() => setShowAvatarPicker(true)}>
                <div className="profilePage-avatar-gradient-border"></div>
                {renderAvatar()}
            </div>

            {/* Country Select Button */}
            <button className="profilePage-country-button" onClick={() => setShowCountryPopup(true)}>
                {country && country !== '' ? (
                    <>
                        <img
                            src={getFlag(country)}
                            alt={`${country} flag`}
                            className="profilePage-flag-icon"
                        />
                        <span>{t(`countries.${country}`, { defaultValue: countryList.find((c) => c.code === country)?.name })}</span>
                    </>
                ) : (
                    <span>{t('selectCountry')}</span>
                )}
            </button>

            {/* Balance Section */}
            <div className="profilePage-balance-section">
                <div className="profilePage-balance-box" onClick={handleDepositClick}>
                    <button className="profilePage-balance-add">+</button>
                    <div className="profilePage-balance-icon">
                        <img src={isTelegramMiniApp ? commonIcons.starIcon : commonIcons.usdtIcon} alt="Balance" />
                    </div>
                    <div className="profilePage-balance-amount">
                        {formatBalance(isTelegramMiniApp ? wallets.stars : wallets.usdt, !isTelegramMiniApp)}
                    </div>
                </div>

                <div className="profilePage-nickname-container">
                    <input
                        type="text"
                        value={nickname}
                        onChange={handleNicknameChange}
                        className="profilePage-nickname-input"
                        maxLength={20}
                        spellCheck="false"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                    />
                    <div className="profilePage-nickname-hint">{t('changeNickname')}</div>
                </div>

                <div className="profilePage-balance-box" onClick={handleTokenClick}>
                    <div className="profilePage-balance-icon">
                        <img src={commonIcons.tokenYellow} alt="Tokens" />
                    </div>
                    <div className="profilePage-balance-amount">
                        {formatBalance(wallets.token)}
                    </div>  
                </div>
            </div>

            {/* Recent Games Section */}
            <div className="profilePage-recent-games">
                {loadingGames ? (
                    <div className="profilePage-game-loading-message">{t('loading')}</div>
                ) : recentGames.length > 0 ? (
                    <div className="profilePage-game-list games-animate-in">
                        {Object.entries(groupGamesByDate(recentGames)).map(([date, games]) => (
                            <div key={date} className="profilePage-game-group">
                                <h3 className="profilePage-game-group-date">{date}</h3>
                                {games.map(game => (
                                    <div key={game._id} className="profilePage-game-item">
                                        <div className="profilePage-game-info">
                                            <div className="profilePage-game-name">
                                                {t(`games.${game.gameName}`, { defaultValue: game.gameName })}
                                            </div>
                                            <div className="profilePage-game-date">
                                                {game.formattedTime}
                                            </div>
                                        </div>
                                        <div className="profilePage-game-result">
                                            <span className={`profilePage-game-result-value ${game.result.toLowerCase()}`}>
                                                {game.result === 'win' ? '+' : '-'}
                                                {game.currency === 'usdt' ? game.bet.toFixed(2) : game.bet}
                                            </span>
                                            <img 
                                                src={
                                                    game.currency === 'token' ? commonIcons.tokenYellow :
                                                    game.currency === 'stars' ? commonIcons.starIcon :
                                                    commonIcons.usdtIcon
                                                }
                                                alt={game.currency}
                                                className={`profilePage-game-currency-icon ${game.currency === 'token' ? 'token' : ''}`}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="profilePage-game-empty-message">{t('noRecentGames')}</div>
                )}
            </div>

            {/* Country Selection Popup */}
            {showCountryPopup && (
                <div className="profilePage-popup-overlay" onClick={() => setShowCountryPopup(false)}>
                    <div className="profilePage-popup-container" onClick={(e) => e.stopPropagation()}>
                        <div className="profilePage-popup-header">
                            <h3 className="profilePage-popup-title">{t('selectCountry')}</h3>
                            <button className="profilePage-popup-close" onClick={() => setShowCountryPopup(false)}>
                                &times;
                            </button>
                        </div>
                        <div className="profilePage-popup-scroll-container">
                            <div className="profilePage-country-list">
                                {countryList.map((countryItem) => (
                                    <div
                                        key={countryItem.code}
                                        className="profilePage-country-item"
                                        onClick={() => handleCountrySelect(countryItem.code)}
                                    >
                                        {countryItem.code !== '' && (
                                            <img
                                                src={getFlag(countryItem.code)}
                                                alt={`${countryItem.name} flag`}
                                                className="profilePage-flag-icon"
                                            />
                                        )}
                                        <span>{t(`countries.${countryItem.code}`, { defaultValue: countryItem.name })}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showAvatarPicker && (
    <div className="profilePage-popup-overlay" onClick={() => setShowAvatarPicker(false)}>
        <div className="profilePage-popup-container" onClick={(e) => e.stopPropagation()}>
            <div className="profilePage-popup-header">
                <h3 className="profilePage-popup-title">{t('selectAvatar')}</h3>
                <button className="profilePage-popup-close" onClick={() => setShowAvatarPicker(false)}>
                    &times;
                </button>
            </div>
            <div className="profilePage-popup-scroll-container">
                <div className="profilePage-avatar-list">
                    
                    <div className="profilePage-avatar-option" onClick={() => handleAvatarSelect(null)}>
                        {user?.photoUrl ? (
                            <img src={user.photoUrl} className="profilePage-avatar-option-imige" />
                        ) : (
                            <div className="profilePage-avatar-placeholder" style={ABG(user?.nickname?.charAt(0) || 'P')}>
                                {user?.nickname?.charAt(0).toUpperCase() || 'P'}
                            </div>
                        )}
                        <span>{t('defaultAvatar')}</span>
                    </div>

                    
                    {Array.from({ length: 26 }).map((_, i) => {
                        const avatarUrl = `${API_BASE_URL}/avatars/avatar${i + 1}.png`;
                        return (
                            <div key={i} className="profilePage-avatar-option" onClick={() => handleAvatarSelect(avatarUrl)}>
                                <img src={avatarUrl} alt={`Avatar ${i + 1}`} className="profilePage-avatar-option-imige" />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    </div>
)}
        </div>
    );
};



const countryList = [
    { code: '', name: 'Not set' }, 
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AL', name: 'Albania' },
    { code: 'AM', name: 'Armenia' },          
    { code: 'AR', name: 'Argentina' },
    { code: 'AT', name: 'Austria' },
    { code: 'AU', name: 'Australia' },
    { code: 'AZ', name: 'Azerbaijan' },       
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BG', name: 'Bulgaria' },          
    { code: 'BR', name: 'Brazil' },           
    { code: 'BY', name: 'Belarus' },
    { code: 'CA', name: 'Canada' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' },             
    { code: 'CO', name: 'Colombia' },
    { code: 'CU', name: 'Cuba' },
    { code: 'CY', name: 'Cyprus' },            
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DE', name: 'Germany' },          
    { code: 'DK', name: 'Denmark' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'EG', name: 'Egypt' },            
    { code: 'ES', name: 'Spain' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },           
    { code: 'GB', name: 'United Kingdom' },   
    { code: 'GE', name: 'Georgia' },           
    { code: 'GR', name: 'Greece' },            
    { code: 'HR', name: 'Croatia' },
    { code: 'HU', name: 'Hungary' },
    { code: 'ID', name: 'Indonesia' },        
    { code: 'IN', name: 'India' },             
    { code: 'IR', name: 'Iran' },              
    { code: 'IQ', name: 'Iraq' },              
    { code: 'IS', name: 'Iceland' },
    { code: 'IT', name: 'Italy' },
    { code: 'JP', name: 'Japan' },             
    { code: 'JO', name: 'Jordan' },            
    { code: 'KE', name: 'Kenya' },
    { code: 'KG', name: 'Kyrgyzstan' },        
    { code: 'KH', name: 'Cambodia' },
    { code: 'KR', name: 'South Korea' },      
    { code: 'KW', name: 'Kuwait' },            
    { code: 'KZ', name: 'Kazakhstan' },
    { code: 'LB', name: 'Lebanon' },          
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'LV', name: 'Latvia' },
    { code: 'MA', name: 'Morocco' },          
    { code: 'MD', name: 'Moldova' },           
    { code: 'ME', name: 'Montenegro' },
    { code: 'MK', name: 'North Macedonia' },
    { code: 'MN', name: 'Mongolia' },
    { code: 'MX', name: 'Mexico' },            
    { code: 'MY', name: 'Malaysia' },
    { code: 'NG', name: 'Nigeria' },           
    { code: 'NL', name: 'Netherlands' },
    { code: 'NO', name: 'Norway' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'OM', name: 'Oman' },             
    { code: 'PE', name: 'Peru' },
    { code: 'PH', name: 'Philippines' },      
    { code: 'PK', name: 'Pakistan' },        
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'QA', name: 'Qatar' },             
    { code: 'RO', name: 'Romania' },          
    { code: 'RS', name: 'Serbia' },           
    { code: 'RU', name: 'Russia' },           
    { code: 'SA', name: 'Saudi Arabia' },     
    { code: 'SD', name: 'Sudan' },
    { code: 'SE', name: 'Sweden' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SY', name: 'Syria' },             
    { code: 'TH', name: 'Thailand' },          
    { code: 'TN', name: 'Tunisia' },           
    { code: 'TR', name: 'Türkiye' },           
    { code: 'TZ', name: 'Tanzania' },
    { code: 'UA', name: 'Ukraine' },           
    { code: 'UG', name: 'Uganda' },
    { code: 'US', name: 'United States' },     
    { code: 'UY', name: 'Uruguay' },
    { code: 'UZ', name: 'Uzbekistan' },        
    { code: 'VE', name: 'Venezuela' },
    { code: 'VN', name: 'Vietnam' },           
    { code: 'YE', name: 'Yemen' },            
    { code: 'ZA', name: 'South Africa' },     
    { code: 'ZW', name: 'Zimbabwe' }
];


export default Profile;